/**
 * WorkspacePage — 2-column workspace mode for deep-work on a field.
 *
 * Route: /workspace/:subProjectId/:field
 * Query params: ?solutionId=, ?step=
 *
 * Layout: Chat (~30%) | Tabbed right pane (~70%)
 * Tabs: Draft | Documents (KB) | FTO Scope (fto_analysis only)
 * Header: WorkspaceHeader with back link, Pilot slide-over button.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { subProjectService } from '../services/subProjectService';
import { solutionService } from '../services/solutionService';
import { knowledgeBaseService } from '../services/knowledgeBaseService';

import WorkspaceHeader from '../components/WorkspaceHeader';
import FieldDraftPane from '../components/FieldDraftPane';
import DirectFieldPane from '../components/DirectFieldPane';
import KBDocumentsPane from '../components/KBDocumentsPane';
import FtoScopePane from '../components/FtoScopePane';
import SpeakingChatbotV2Assistant from '../components/SpeakingChatbotV2Assistant';
import PilotSlideOver from '../components/PilotSlideOver';
import PatentabilityPane from '../components/PatentabilityPane';
import FtoClaimsAnalysisPane from '../components/FtoClaimsAnalysisPane';
import ClaimsPane from '../components/ClaimsPane';
import PatentDraftPane from '../components/PatentDraftPane';
import IndustrializationPane from '../components/IndustrializationPane';
import { useToast } from '../components/ToastProvider';
import { getActiveFieldForStep } from '../configs/stepRegistry';
import { DRAFT_FIELDS, FIELD_LABELS, FIELD_TO_RELEVANCE_CONTEXT } from '../configs/fieldConfig';
import SotaStepper from '../components/sota/SotaStepper';
import SotaSearchPanel from '../components/sota/SotaSearchPanel';
import OpsRetrievalModal from '../components/OpsRetrievalModal';
import OpenalexRetrievalModal from '../components/OpenalexRetrievalModal';
import FullKBReviewLaunchModal from '../components/FullKBReviewLaunchModal';
import FieldUpdateFlow from '../components/FieldUpdateFlow';
import SearchResultsTab from '../components/SearchResultsTab';
import type { SearchResultsData } from '../components/SearchResultsTab';
import { sotaService } from '../services/sotaService';
import { useSearchActions } from '../hooks/useSearchActions';
import { buildWorkspaceRightTabs } from '../utils/workspaceTabs';

const WorkspacePage: React.FC = () => {
  const { subProjectId, field } = useParams<{ subProjectId: string; field: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const solutionId = searchParams.get('solutionId');
  const isFtoField = field === 'fto_analysis';
  const isPatentabilityField = field === 'patentability';
  const isClaimsField = field === 'claims';
  const isPatentApplicationField = field === 'patent_application';
  const isIndustrializationField = field === 'industrialization';
  const isSotaField = field === 'state_of_the_art';

  // Entity data
  const [spName, setSpName] = useState('');
  const [kbId, setKbId] = useState<string | null>(null);
  const [kbName, setKbName] = useState('');
  const [kbOptions, setKbOptions] = useState<{ id: string; name: string }[]>([]);
  const [solutionName, setSolutionName] = useState('');
  const [projectId, setProjectId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  // Relevance context of this workspace (document_relevance context_field)
  const relevanceContext = (field && FIELD_TO_RELEVANCE_CONTEXT[field]) || 'state_of_the_art';

  // Right pane tabs (non-SOTA fields; SOTA uses its own sotaActiveTab state below)
  const [activeRightTab, setActiveRightTab] = useState<'draft' | 'documents' | 'fto' | 'fto_claims' | 'patentability' | 'patent_draft' | 'industrialization'>(
    isIndustrializationField ? 'industrialization' : 'draft'
  );

  // Draft reload trigger (incremented when WS sends draft_updated)
  const [draftReloadTrigger, setDraftReloadTrigger] = useState(0);

  // FTO reload trigger (incremented when WS sends fto_scope_updated)
  const [ftoReloadTrigger, setFtoReloadTrigger] = useState(0);

  // Patentability pane does not yet have a reload mechanism; kept as plain constant
  const patentabilityReloadTrigger = 0;

  // Pilot slide-over
  const [pilotOpen, setPilotOpen] = useState(false);

  // SOTA — separate tab + data state (not mixed with typed activeRightTab)
  const [sotaActiveTab, setSotaActiveTab] = useState<string>('draft');
  const [sotaContent, setSotaContent] = useState('');   // published field value (for FUF)
  const [sotaStatus, setSotaStatus] = useState<'empty' | 'scoping' | 'draft' | 'enriched' | 'finalized'>('empty');
  const [sotaDocsTabs, setSotaDocsTabs] = useState<SearchResultsData[]>([]);
  const [sotaSelectedDocs, setSotaSelectedDocs] = useState<Record<string, Set<string>>>({});
  const [showOpsModal, setShowOpsModal] = useState(false);
  const [showOpenalexModal, setShowOpenalexModal] = useState(false);
  const [showFullKBReviewModal, setShowFullKBReviewModal] = useState(false);
  const [showFufFlow, setShowFufFlow] = useState(false);

  const { addToast } = useToast();

  // Docs{i} save-to-KB hook (SOTA only)
  const [, _setSotaKbsData] = useState<unknown[]>([]);
  const { handleSaveSearchResults } = useSearchActions(
    subProjectId, kbId, kbName,
    sotaDocsTabs, setSotaDocsTabs,
    _setSotaKbsData, setSotaActiveTab,
  );

  // Load context on mount
  useEffect(() => {
    if (!subProjectId) return;
    setLoading(true);

    (async () => {
      try {
        const sp = await subProjectService.getById(subProjectId);
        setSpName(sp.name || subProjectId);
        setProjectId(sp.project_id);

        if (isSotaField) {
          const sotaRaw = (sp as unknown as Record<string, unknown>).state_of_the_art as { content?: string; generation_status?: string } | null;
          setSotaContent(sotaRaw?.content || '');
          setSotaStatus((sotaRaw?.generation_status as 'empty' | 'scoping' | 'draft' | 'enriched' | 'finalized') || 'empty');
        }

        // Load solution (name + context KBs)
        let sol: { name?: string; context_kbs?: Record<string, string | null> } | undefined;
        if (solutionId) {
          try {
            const sols = await solutionService.list(subProjectId);
            sol = sols.find(s => s.id === solutionId);
            setSolutionName(sol?.name || '');
          } catch { /* ignore */ }
        }

        // Candidate KBs for the Documents tab: the workspace's dedicated context KB
        // (default when present), then the SP's main KBs. Deduplicated, names fetched.
        const contextKbId = sol?.context_kbs?.[relevanceContext] || undefined;
        const spKbIds: string[] = sp.kb_ids || [];
        const candidateIds = [...new Set([contextKbId, ...spKbIds].filter(Boolean))] as string[];
        const options: { id: string; name: string }[] = [];
        for (const id of candidateIds) {
          try {
            const kb = await knowledgeBaseService.getById(id);
            options.push({ id, name: kb.name || id });
          } catch { /* KB fetch failed — omit from selector */ }
        }
        setKbOptions(options);
        // SOTA keeps its historical default (first main KB, used by the save-to-KB flow);
        // solution workspaces default to their dedicated context KB when it exists.
        const defaultId = isSotaField ? (spKbIds[0] || options[0]?.id) : options[0]?.id;
        if (defaultId) {
          setKbId(defaultId);
          setKbName(options.find(o => o.id === defaultId)?.name || '');
        }
      } catch (e) {
        console.error('WorkspacePage: failed to load context', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [subProjectId, solutionId, field]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleKbChange = useCallback((id: string) => {
    setKbId(id);
    setKbName(kbOptions.find(o => o.id === id)?.name || '');
  }, [kbOptions]);

  const activeField = field || null;
  const fieldLabel = activeField ? (FIELD_LABELS[activeField] || activeField) : '';
  const isDraftField = activeField ? DRAFT_FIELDS.has(activeField) : false;

  // SOTA: handle extraction results from chatbot → create Docs{i} tab
  const handleSotaExtractionResults = useCallback((data: unknown) => {
    const d = data as { tabId?: string; tabName?: string; kbId?: string; kbName?: string; destinationKbId?: string; destinationKbName?: string; documents?: Array<{ document_id: string; document_title?: string; relevance_score?: number; justification?: string }>; timestamp?: string };
    if (!d.tabId || !d.tabName) return;
    setSotaDocsTabs(prev => {
      if (prev.find(t => t.tabId === d.tabId)) { setSotaActiveTab(d.tabId!); return prev; }
      const newTab: SearchResultsData = {
        tabId: d.tabId!, tabName: d.tabName!,
        kbId: d.kbId || '', kbName: d.kbName || '',
        destinationKbId: d.destinationKbId, destinationKbName: d.destinationKbName,
        searchType: 'extraction',
        documents: (d.documents || []).map(doc => ({
          document_id: doc.document_id, document_title: doc.document_title,
          relevance_score: doc.relevance_score ?? 0, justification: doc.justification,
        })),
        timestamp: d.timestamp || new Date().toISOString(),
        isSaved: false,
      };
      setSotaActiveTab(d.tabId!);
      return [...prev, newTab];
    });
  }, []);

  const handleBackToOverview = () => {
    navigate(`/scv2/sub-project/${subProjectId}`);
  };

  const handleDraftUpdated = useCallback(() => {
    setDraftReloadTrigger(prev => prev + 1);
  }, []);

  const handleFtoUpdated = useCallback(() => {
    setFtoReloadTrigger(prev => prev + 1);
  }, []);

  const handleAIFieldUpdate = useCallback((fieldName: string, value: string) => {
    if (fieldName === 'name') setSolutionName(value);
    // Draft fields are otherwise handled by the draft pane
  }, []);

  const handlePublished = useCallback((_publishedField: string, _content: string) => {}, []);

  if (!subProjectId || !field) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-slate-400 text-sm">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  // Non-SOTA tabs only; SOTA tab bar is rendered directly in the right pane below.
  // Tab order/composition lives in buildWorkspaceRightTabs (unit tested there);
  // FTO Workspace puts Draft last, every other field keeps Draft first.
  const rightTabs = buildWorkspaceRightTabs({
    t,
    kbId,
    kbName,
    isFtoField,
    isPatentabilityField,
    isPatentApplicationField,
    isIndustrializationField,
  });

  // Chatbot props that differ between claims and other fields
  const chatbotExtractionResults = isSotaField ? handleSotaExtractionResults : () => {};
  const chatbotSwitchTab = isSotaField ? (tab: string) => setSotaActiveTab(tab) : undefined;
  const chatbotInitialMessage = (!isClaimsField && field === 'solution_description')
    ? t('workspace.solutionDesc.initialMessage', 'Share your ideas freely — I will help structure them into a solution description.')
    : undefined;

  const chatbotPane = (
    <div className="w-[30%] flex-shrink-0 flex flex-col border-r border-slate-200 min-h-0 overflow-hidden">
      <SpeakingChatbotV2Assistant
        projectId={projectId}
        entityType="sub-project"
        entityId={subProjectId}
        entityName={spName}
        fieldConfig={{}}
        fieldLabels={{}}
        kbId={kbId || undefined}
        kbName={kbName}
        projectName={spName}
        documentIds={[]}
        language={i18n.language as 'en' | 'fr'}
        activeTab="metadata"
        activeField={activeField}
        onFieldUpdate={handleAIFieldUpdate}
        getFieldValue={() => ''}
        onContextFieldChanged={() => {}}
        onExtractionResults={chatbotExtractionResults}
        onSwitchTab={chatbotSwitchTab}
        onPilotHandoff={handleBackToOverview}
        showMetrics={user?.role === 'SysAdmin' || user?.role === 'DevOps'}
        solutionId={solutionId || undefined}
        onDraftUpdated={handleDraftUpdated}
        onFtoUpdated={handleFtoUpdated}
        initialMessage={chatbotInitialMessage}
        proactiveInit={isSotaField}
      />
    </div>
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <WorkspaceHeader
        subProjectName={spName}
        fieldLabel={fieldLabel}
        kbName={kbName || undefined}
        solutionName={solutionName || undefined}
        onBackToOverview={handleBackToOverview}
        onOpenPilot={() => setPilotOpen(true)}
      />

      {isClaimsField ? (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {chatbotPane}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <ClaimsPane
              subProjectId={subProjectId}
              solutionId={solutionId || ''}
              spName={spName}
              solutionName={solutionName || undefined}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {chatbotPane}

          {/* Right pane — SOTA or generic */}
          {isSotaField ? (
            /* ── SOTA dedicated right pane (Phases 1-4) ── */
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <SotaStepper generationStatus={sotaStatus} />

              {/* SOTA tab bar: static tabs + dynamic Docs{i} bookmark tabs */}
              <div className="flex items-center border-b border-slate-200 bg-slate-50 flex-shrink-0 px-2 overflow-x-auto">
                {([
                  { id: 'draft',   label: t('sota.panel.tab.draft',    'Draft') },
                  ...(kbId ? [{ id: 'documents', label: kbName || t('sota.panel.tab.documents', 'Documents') }] : []),
                  { id: 'addocs', label: t('sota.panel.tab.addocs',   'Ajout docs') },
                ] as { id: string; label: string }[]).map(tab => (
                  <button key={tab.id} onClick={() => setSotaActiveTab(tab.id)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${sotaActiveTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    {tab.label}
                  </button>
                ))}
                {sotaDocsTabs.map(tab => (
                  <span key={tab.tabId} className="flex items-center flex-shrink-0">
                    <button onClick={() => setSotaActiveTab(tab.tabId)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${sotaActiveTab === tab.tabId ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                      {tab.tabName}{tab.isSaved ? ' ✓' : ''}
                    </button>
                    <button onClick={() => setSotaDocsTabs(prev => prev.filter(t => t.tabId !== tab.tabId))}
                      className="px-1 text-slate-400 hover:text-slate-600 text-xs leading-none">×</button>
                  </span>
                ))}
              </div>

              {/* SOTA tab content */}
              <div className="flex-1 min-h-0 overflow-hidden">
                {sotaActiveTab === 'draft' && (
                  <FieldDraftPane
                    subProjectId={subProjectId} field={field} fieldLabel={fieldLabel}
                    projectId={projectId} solutionId={solutionId}
                    onPublished={handlePublished} reloadTrigger={draftReloadTrigger}
                    onExport={() => sotaService.exportDocx(subProjectId)}
                    exportLabel={t('sota.export.button', 'Export as .docx')}
                  />
                )}
                {sotaActiveTab === 'documents' && kbId && (
                  <KBDocumentsPane
                    kbId={kbId} kbName={kbName} subProjectId={subProjectId}
                    solutionId={solutionId} relevanceContext={relevanceContext}
                    kbOptions={kbOptions} onKbChange={handleKbChange}
                    isFtoField={false}
                    ftoReloadTrigger={0} onFtoPatentsChanged={() => {}}
                    onLaunchFullKBReview={() => setShowFullKBReviewModal(true)}
                    onLaunchFuf={() => setShowFufFlow(true)}
                  />
                )}
                {sotaActiveTab === 'addocs' && (
                  <SotaSearchPanel
                    subProjectId={subProjectId}
                    onOpenOpsModal={() => setShowOpsModal(true)}
                    onOpenOpenalexModal={() => setShowOpenalexModal(true)}
                    onNavigateToKSearch={() => navigate(`/semantic-search/${subProjectId}`)}
                    onSearchLaunched={() => addToast({
                      type: 'info',
                      title: t('sota.panel.search.launched', 'Search launched'),
                      message: t('sota.panel.search.launchedMsg', 'Results will appear in a new Docs tab when the search is complete.'),
                    })}
                  />
                )}
                {/* Docs{i} result tabs */}
                {sotaDocsTabs.map(tab => sotaActiveTab === tab.tabId && (
                  <SearchResultsTab
                    key={tab.tabId} data={tab}
                    selectedDocuments={sotaSelectedDocs[tab.tabId] || new Set<string>()}
                    onSelectionChange={(ids) => setSotaSelectedDocs(prev => ({ ...prev, [tab.tabId]: new Set(ids) }))}
                    onSave={(tabId, ids) => handleSaveSearchResults(tabId, ids)}
                    selectedKbId={kbId} selectedKbName={kbName}
                    subProjectId={subProjectId}
                  />
                ))}
              </div>
            </div>
          ) : (
            /* ── Generic tabbed right pane (all non-SOTA fields) ── */
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {rightTabs.length > 1 && (
                <div className="flex items-center gap-0 border-b border-slate-200 bg-slate-50 flex-shrink-0 px-2">
                  {rightTabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveRightTab(tab.id)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${activeRightTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-hidden">
                {activeRightTab === 'draft' && (
                  isDraftField
                    ? <FieldDraftPane subProjectId={subProjectId} field={field} fieldLabel={fieldLabel} projectId={projectId} solutionId={solutionId} onPublished={handlePublished} reloadTrigger={draftReloadTrigger} />
                    : <DirectFieldPane subProjectId={subProjectId} field={field} fieldLabel={fieldLabel} solutionId={solutionId} />
                )}
                {activeRightTab === 'documents' && kbId && (
                  <KBDocumentsPane kbId={kbId} kbName={kbName} subProjectId={subProjectId}
                    solutionId={solutionId} relevanceContext={relevanceContext}
                    kbOptions={kbOptions} onKbChange={handleKbChange}
                    isFtoField={isFtoField}
                    ftoReloadTrigger={ftoReloadTrigger} onFtoPatentsChanged={handleFtoUpdated} />
                )}
                {activeRightTab === 'fto' && isFtoField && solutionId && (
                  <FtoScopePane subProjectId={subProjectId} solutionId={solutionId} reloadTrigger={ftoReloadTrigger} />
                )}
                {activeRightTab === 'fto_claims' && isFtoField && solutionId && (
                  <FtoClaimsAnalysisPane subProjectId={subProjectId} solutionId={solutionId} />
                )}
                {activeRightTab === 'patentability' && isPatentabilityField && solutionId && (
                  <PatentabilityPane subProjectId={subProjectId} solutionId={solutionId} reloadTrigger={patentabilityReloadTrigger} />
                )}
                {activeRightTab === 'patent_draft' && isPatentApplicationField && solutionId && (
                  <PatentDraftPane subProjectId={subProjectId} solutionId={solutionId} />
                )}
                {activeRightTab === 'industrialization' && isIndustrializationField && solutionId && (
                  <IndustrializationPane subProjectId={subProjectId} solutionId={solutionId} />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SOTA modals */}
      {isSotaField && showOpsModal && (
        <OpsRetrievalModal subProjectId={subProjectId} onClose={() => setShowOpsModal(false)} />
      )}
      {isSotaField && showOpenalexModal && (
        <OpenalexRetrievalModal subProjectId={subProjectId} onClose={() => setShowOpenalexModal(false)} />
      )}
      {isSotaField && (
        <FullKBReviewLaunchModal
          isOpen={showFullKBReviewModal}
          subProjectId={subProjectId}
          onClose={() => setShowFullKBReviewModal(false)}
          onLaunched={(reviewId) => {
            setShowFullKBReviewModal(false);
            navigate(`/full-kb-review/${subProjectId}/${reviewId}`);
          }}
        />
      )}
      {/* Phase 4: FUF (Field Update Flow) for SOTA — launched from Documents tab */}
      {isSotaField && showFufFlow && (
        <FieldUpdateFlow
          subProjectId={subProjectId}
          fieldName="state_of_the_art"
          fieldValue={sotaContent}
          onAccept={async (newText) => {
            setShowFufFlow(false);
            setSotaContent(newText);
            try {
              await subProjectService.update(subProjectId, {
                state_of_the_art: { content: newText, generation_status: 'enriched' } as never,
              });
            } catch { /* ignore — field update service already saved via its own endpoint */ }
          }}
          onClose={() => setShowFufFlow(false)}
        />
      )}

      {/* Pilot slide-over */}
      {pilotOpen && (
        <PilotSlideOver
          subProjectId={subProjectId}
          subProjectName={spName}
          kbId={kbId || undefined}
          kbName={kbName}
          solutionId={solutionId}
          language={i18n.language}
          onClose={() => setPilotOpen(false)}
          onStepOpen={(event) => {
            setPilotOpen(false);
            const newField = getActiveFieldForStep(event.stepId);
            if (newField && newField !== field) {
              navigate(`/workspace/${subProjectId}/${newField}?step=${event.stepId}${event.solutionId ? `&solutionId=${event.solutionId}` : ''}`);
            }
          }}
        />
      )}
    </div>
  );
};

export default WorkspacePage;
